import * as core from "@graffer-inc/cloud-gov-core";
import { BaseType } from "../base/base-type";
import {
  ReadonlyFileRefKeyRegistry,
  MaxPaymentRequest,
  ApplicationContentType,
  PaymentStatus,
} from "@graffer-inc/cloud-gov-core";
import { ReadonlyFetchMyNumberExchanger } from "../form-model/readonly-fetch-mynumber-exchanger";
export type ApplyStatus = core.ApplyStatus;
export type SummarizedPaymentStatus = core.SummarizedPaymentStatus;
export type ApplicationType = core.ApplicationType & {
  applicantContent?: ApplicationContentType;
};
export const AllStatuses: ApplyStatus[] = [
  "UNTREATED",
  "WIP",
  "CLOSED_COMPLETED",
  "CLOSED_WITHDRAWN",
  "CLOSED_REJECTED",
];
export type ModifiedApplicationContentType =
  core.ModifiedApplicationContentType;
export type ModifyInfoType = {
  modificationType: core.ModificationType;
  internalComment: string;
};

export class ApplicationEntity extends core.ApplicationEntity {
  public readonly applicantContent?: ApplicationContentType;
  constructor(values: ApplicationType) {
    super(values);
    this.applicantContent = values.applicantContent;
  }

  formatFileURLWithRefKey(refKey: string) {
    const fileIndex = this.files.findIndex((f) => f.refKey === refKey);
    if (fileIndex === undefined) {
      return null;
    }
    return this.formatFileURLWithIndex(fileIndex);
  }

  formatFileURLWithIndex(index: number) {
    return encodeURI(`/files/applications/${this.id}/file?index=${index}`);
  }

  formatFileResourcesURLWithIndex(index: number) {
    return encodeURI(`/applications/${this.id}/file?index=${index}`);
  }

  formatIssueFileURLWithIndex(index: number) {
    return encodeURI(
      `/files/applications/${this.id}/issue-file?index=${index}`,
    );
  }

  getApplicantContent(): ApplicationContentType {
    return (
      this.applicantContent || {
        jsonDocument: this.jsonDocument,
        files: this.files,
        myNumbers: this.myNumbers,
      }
    );
  }

  isPaymentAddable() {
    return (
      this.status === "WIP" && this.getPaymentsV2().length < MaxPaymentRequest
    );
  }

  /**
   * Refundが済んでいない決済があるどうか
   */
  hasRefundablePayment() {
    return (
      this.getPaymentsV2().filter((v) => v.amountCharged - v.amountRefunded > 0)
        .length > 0
    );
  }

  /**
   * ペイジー決済があるどうか
   */
  hasPayeasyPayment() {
    return (
      this.getPaymentsV2().filter((v) =>
        v.acceptedPaymentMethods
          ? v.acceptedPaymentMethods.includes("payeasy")
          : false,
      ).length > 0
    );
  }

  // 決済状況ステータス
  // 複数決済を統括した 決済待ち:WAITING/完了:DONE/入金不足:DEFICIENCY_PAYMENT/過入金：EXCEEDING_PAYMENT/キャンセル:CANCELLED のステータス
  // 決済が存在しない場合は決済なし:NONEを返却
  summarizedPaymentStatus(): core.SummarizedPaymentStatus {
    if (this.paymentsV2!.length === 0) return "NONE";
    const has = (status: PaymentStatus) => {
      return this.paymentsV2!.some((p) => p.status === status);
    };
    if (has("exceeding_payment")) {
      return "EXCEEDING_PAYMENT";
    }
    if (has("deficient_payment")) {
      return "DEFICIENCY_PAYMENT";
    }
    if (has("requested")) {
      return "WAITING";
    }
    const isCancel = !this.paymentsV2!.some((p) =>
      ["paid", "partial_refunded"].includes(p.status),
    );
    if (isCancel) {
      return "CANCELLED";
    }
    return "DONE";
  }

  isWip() {
    return this.status === "WIP";
  }

  isDone() {
    switch (this.status) {
      case "WIP":
      case "UNTREATED":
        return false;
      case "CLOSED_COMPLETED":
      case "CLOSED_REJECTED":
      case "CLOSED_WITHDRAWN":
        return true;
    }
  }

  public changeStatus(status: ApplyStatus) {
    // TODO: Stop status change from CLOSED_* to Others.
    this.status = status;
  }

  changableStatus(): ApplyStatus[] {
    if (this.isDone()) {
      return [];
    }

    // application waiting for verification to finish should not be able to change status
    if (this.hasSignature() && this.signatureStatus() === "IS_PENDING") {
      return [];
    }
    // since all application will be "IS_INVALID" in staging environment,
    // for the ease of status update test, allow the status change for local, dev, stg
    if (
      process.env.REACT_APP_ENVIRONMENT === "production" &&
      this.hasSignature() &&
      this.signatureStatus() === "IS_INVALID"
    ) {
      // only allow CLOSED_REJECTED for application with invalid signature.
      return ["CLOSED_REJECTED"];
    }

    // cannot select anything exept WIP when there's requested payment
    if (
      this.paymentsV2!.some((p) =>
        ["requested", "exceeding_payment", "deficient_payment"].includes(
          p.status,
        ),
      )
    ) {
      return this.status === "WIP" ? [] : ["WIP"];
    }

    return AllStatuses.filter((s) => this.status !== s);
  }

  copy() {
    return new ApplicationEntity({
      ...this,
      createdAtUnixSecond: this.createdAtDate.getTime() / 1000,
      updatedAtUnixSecond: this.updatedAtDate.getTime() / 1000,
    });
  }

  toRefKeyExchanger() {
    return new ReadonlyFileRefKeyRegistry(this);
  }

  toMyNumberRefKeyExchanger() {
    return new ReadonlyFetchMyNumberExchanger(this.myNumbers || [], this.id);
  }

  static fromJSON(json: ApplicationType) {
    return new ApplicationEntity(json);
  }

  static fromJSONArray(json?: ApplicationType[]) {
    if (!json) {
      return [];
    }
    return json.map(ApplicationEntity.fromJSON);
  }
}

export type ApplicantTypeLiterals = core.ApplicantTypeLiterals;

export class ApplicantTypeState extends BaseType<ApplicantTypeLiterals> {}

export type Applicant = core.Applicant;
export type IssueFile = core.IssueFile;
export type UploadIssueFile = {
  type: core.MimeType;
  name: string;
  data: string;
  isLgpkiSigned: boolean;
};

export type UploadIssueFileStream = {
  type: core.MimeType;
  name: string;
};

export type ModifiedFile = core.ModifiedFile;
export type Signature = core.Signatures;
